import { http } from '@/lib/api'
import type {
  CreateTimeEntryInput,
  IsoDate,
  Me,
  PickerData,
  SetAbsenceInput,
  TeamDayResponse,
  TeamWeekResponse,
  TimeEntry,
  UpdateTimeEntryInput,
  UtilizationReport,
  WeekApproval,
  WeekApprovalPeriod,
  WeekHistoryResponse,
  WeekResponse,
  WeekTotalsResponse,
  WorkdayException,
} from '@/lib/types'
import type { TimeApi, UtilizationParams } from './adapter'

export class RealApi implements TimeApi {
  me() {
    return http.get<Me>('/api/me')
  }

  week(weekId: string, personId?: string) {
    return http.get<WeekResponse>(`/api/time-entries/week/${weekId}`, { personId })
  }

  weekTotals(weekId: string, personId?: string) {
    return http.get<WeekTotalsResponse>(`/api/time-entries/week-totals/${weekId}`, { personId })
  }

  weekHistory(weekId: string, personId?: string) {
    return http.get<WeekHistoryResponse>(`/api/time-entries/history/${weekId}`, { personId })
  }

  teamWeek(weekId: string) {
    return http.get<TeamWeekResponse>(`/api/team/week/${weekId}`)
  }

  teamDay(date: string) {
    return http.get<TeamDayResponse>(`/api/team/day/${date}`)
  }

  teamWeekTotals(weekId: string) {
    return http.get<WeekTotalsResponse>(`/api/team/week-totals/${weekId}`)
  }

  pickers(personId?: string) {
    return http.get<PickerData>('/api/time-entries/pickers', { personId })
  }

  createEntry(input: CreateTimeEntryInput) {
    return http.post<TimeEntry>('/api/time-entries', input)
  }

  updateEntry(id: string, patch: UpdateTimeEntryInput) {
    return http.patch<TimeEntry>(`/api/time-entries/${id}`, patch)
  }

  deleteEntry(id: string) {
    return http.del<void>(`/api/time-entries/${id}`)
  }

  approveWeek(weekId: string, personId?: string, through?: IsoDate) {
    return http.post<WeekApproval>(`/api/week-approvals/${weekId}/approve`, {
      ...(personId ? { personId } : {}),
      ...(through ? { through } : {}),
    })
  }

  reopenWeek(weekId: string, personId?: string) {
    return http.post<WeekApproval>(`/api/week-approvals/${weekId}/reopen`, personId ? { personId } : {})
  }

  weekApprovalPeriod(start: string, end: string) {
    return http.get<WeekApprovalPeriod>('/api/week-approvals/period', { start, end })
  }

  utilizationReport(params: UtilizationParams) {
    return http.get<UtilizationReport>('/api/reports/utilization', {
      personId: params.personId,
      period: params.period,
      anchor: params.anchor,
    })
  }

  async workdayExceptions(from: string, to: string, personId?: string) {
    const res = await http.get<{ exceptions: WorkdayException[] }>('/api/workday-exceptions', { from, to, personId })
    return res.exceptions
  }

  async setAbsence(input: SetAbsenceInput) {
    const res = await http.post<{ exceptions: WorkdayException[] }>('/api/workday-exceptions', input)
    return res.exceptions
  }

  deleteAbsence(id: string) {
    return http.del<void>(`/api/workday-exceptions/${id}`)
  }
}
